import { useCallback, useEffect } from 'react';

import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Label } from '@components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useDrogasAntiEpileticas } from './context';
import {
  EEpilepsyType,
  EPatientType,
} from './context/IUseDrogasAntiEpileticas';

const formSchema = z.object({
  patientType: z.enum(EPatientType, {
    message: errorsValidation.required,
  }),
  epilepsyType: z.enum(EEpilepsyType, {
    message: errorsValidation.required,
  }),
  firstLineDrug: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function AntiEpilepticDrugs() {
  const { user } = useAuth();
  const {
    combination,
    epilepsyType,
    firstLineDrugs,
    onGetDrugs,
    onSelectFirstLineDrug,
    patientType,
    setEpilepsyType,
    setPatientType,
  } = useDrogasAntiEpileticas();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      patientType,
      epilepsyType,
      firstLineDrug: firstLineDrugs.length ? firstLineDrugs[0].name : '',
    },
  });

  const handleChangeFirstLineDrug = useCallback(
    (value: string): void => {
      onSelectFirstLineDrug(value);
    },
    [onSelectFirstLineDrug],
  );

  const handleChangePatientType = useCallback(
    (value: EPatientType): void => {
      setPatientType(value);
      onGetDrugs({
        epilepsyType,
        patientType: value,
      });
    },
    [epilepsyType, onGetDrugs, setPatientType],
  );

  const handleChangeEpilepsyType = useCallback(
    (value: EEpilepsyType): void => {
      setEpilepsyType(value);
      onGetDrugs({
        epilepsyType: value,
        patientType,
      });
    },
    [onGetDrugs, patientType, setEpilepsyType],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'patientType' && value.patientType) {
        handleChangePatientType(value.patientType);
      }
      if (name === 'epilepsyType' && value.epilepsyType) {
        handleChangeEpilepsyType(value.epilepsyType);
      }
      if (name === 'firstLineDrug' && value.firstLineDrug) {
        handleChangeFirstLineDrug(value.firstLineDrug);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    if (firstLineDrugs.length) {
      handleChangeFirstLineDrug(firstLineDrugs[0].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Drogas anti-epiléticas: qual escolher?" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="patientType"
              render={({ field }) => (
                <FormItem className="w-full max-w-lg">
                  <FormLabel>Tipo de paciente</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="h-fit text-start"
                        onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label: 'Outro',
                          value: EPatientType.Other,
                        },
                        {
                          label: 'Idoso(a)',
                          value: EPatientType.Elderly,
                        },
                        {
                          label:
                            'Mulher em idade fértil não grávida e não amamentado',
                          value: EPatientType.WomanNotPregnant,
                        },
                        {
                          label: 'Mulher grávida ou amamentado',
                          value: EPatientType.Pregnant,
                        },
                        {
                          label: 'Paciente com depressão',
                          value: EPatientType.PatientWithDepression,
                        },
                        {
                          label: 'Paciente com rebaixamento mental',
                          value: EPatientType.PatientWithMentalImpairment,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="epilepsyType"
              render={({ field }) => (
                <FormItem className="w-full max-w-lg">
                  <FormLabel>Tipo de epilepsia</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="h-fit text-start"
                        onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label:
                            'Epilepsia generalizada idiopática (tônico-clônica)',
                          value:
                            EEpilepsyType.IdiopathicGeneralizedEpilepsyTonicClonic,
                        },
                        {
                          label: 'Epilepsia generalizada idiopática (ausência)',
                          value:
                            EEpilepsyType.IdiopathicGeneralizedEpilepsyAbsence,
                        },
                        {
                          label:
                            'Epilepsia generalizada idiopática (mioclônica)',
                          value:
                            EEpilepsyType.IdiopathicGeneralizedEpilepsyMyoclonic,
                        },
                        {
                          label:
                            'Epilepsia focal sintomática (parcial simples)',
                          value:
                            EEpilepsyType.SymptomaticFocalEpilepsySimplePartial,
                        },
                        {
                          label:
                            'Epilepsia focal sintomática (parcial complexa)',
                          value:
                            EEpilepsyType.SymptomaticFocalEpilepsyComplexPartial,
                        },
                        {
                          label:
                            'Epilepsia focal sintomática (parcial secundariamente generalizada)',
                          value:
                            EEpilepsyType.SymptomaticFocalEpilepsySecondarilyGeneralizedPartial,
                        },
                        {
                          label:
                            'Epilepsia generalizada sintomática (tônico-clônica)',
                          value:
                            EEpilepsyType.SymptomaticGeneralizedEpilepsyTonicClonic,
                        },
                        {
                          label:
                            'Epilepsia generalizada sintomática (ausência)',
                          value:
                            EEpilepsyType.SymptomaticGeneralizedEpilepsyAbsence,
                        },
                        {
                          label:
                            'Epilepsia generalizada sintomática (mioclônica)',
                          value:
                            EEpilepsyType.SymptomaticGeneralizedEpilepsyMyoclonic,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-col items-center justify-center gap-2">
            <Label>Primeira linha monoterapia</Label>
            <div className="border-tertiary flex flex-col rounded-lg border-2 p-4">
              {firstLineDrugs.map((firstLineDrug: any) => (
                <Label className="font-bold" key={firstLineDrug.id}>
                  {firstLineDrug.name}
                </Label>
              ))}
            </div>
          </div>

          <Separator className="my-4" />

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="firstLineDrug"
              render={({ field }) => (
                <FormItem className="w-full max-w-lg">
                  <FormLabel>Escolha um medicamento</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="h-fit text-start"
                        onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {firstLineDrugs
                        .map((firstLineDrug: any) => ({
                          label: firstLineDrug.name,
                          value: firstLineDrug.name,
                        }))
                        .map((item: any) => (
                          <SelectItem
                            className="focus:bg-tertiary"
                            key={item.value}
                            value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          {combination?.presentation && (
            <div className="mx-auto mb-4 flex max-w-lg flex-col space-y-1">
              <Label>Apresentação:</Label>
              <Label className="ml-4 text-sm font-light">
                {combination?.presentation}
              </Label>
            </div>
          )}

          {combination?.posologia && (
            <div className="mx-auto mb-4 flex max-w-lg flex-col space-y-1">
              <Label>Posologia sugerida:</Label>
              <Label className="ml-4 text-sm font-light">
                {combination?.posologia}
              </Label>
            </div>
          )}

          {combination?.drugs && (
            <div className="mx-auto mb-4 flex max-w-lg flex-col space-y-1">
              <Label>Possíveis associações:</Label>
              <Label className="ml-4 text-sm font-light">
                {combination?.drugs}
              </Label>
            </div>
          )}

          {combination?.observation && (
            <div className="mx-auto mb-4 flex max-w-lg flex-col space-y-1">
              <Label>Observações:</Label>
              <Label className="ml-4 text-sm font-light">
                {combination?.observation}
              </Label>
            </div>
          )}

          <div className="mx-auto mb-4 flex max-w-lg flex-col space-y-2">
            <Label className="text-bold text-xs">Considerações</Label>
            <p className="text-foreground text-xs font-light">
              Epilepsia idiopática: são aquelas que ocorrem na ausência de lesão
              estrutural subjacente.
            </p>
            <p className="text-foreground text-xs font-light">
              Epilepsia sintomática: ocorrem devido a lesões, como esclerose
              temporal mesial e tumores cerebrais primários.
            </p>
          </div>

          <div className="mx-auto mb-4 flex max-w-lg flex-col space-y-2">
            <Label className="text-bold text-xs">Referências</Label>
            <p className="text-foreground text-xs font-light">
              1. Tratamento de Epilepsia: Consenso dos Especialistas
              Brasileiros. Arq Neuropsiquiatr 2003;61(4):1045-1070
            </p>
            <p className="text-foreground text-xs font-light">
              2. Manual do Residente de Clínica Médica. Milton de Arruda
              Martins, Barueri, SP. Manole, 2015.
            </p>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default AntiEpilepticDrugs;
